import type { InDivisionRecord } from '../types/standings.types'

/**
 * Surface-coloured gap between touching segments, in px. White space does the
 * separating — never a stroke around each segment, which would add ink that is
 * not data.
 */
export const RECORD_SEGMENT_GAP = 2

/** Corner radius on the outer ends of a bar, in px. */
export const RECORD_BAR_RADIUS = 3

/** Rendered height of a record bar, in px. */
export const RECORD_BAR_HEIGHT = 22

/**
 * Smallest bar width, as a percentage of the track. A team with one game in a
 * division where others have forty still needs room for its rounded ends.
 */
const MIN_BAR_WIDTH_PERCENT = 18

/** The five outcomes, ordered good → bad. This order IS the colour scale. */
export const RECORD_OUTCOMES = [
  'wins',
  'losses',
  'ties',
  'otLosses',
  'soLosses',
] as const

export type RecordOutcome = (typeof RECORD_OUTCOMES)[number]

/** Label for each outcome, used by the legend and the hover tooltips. */
export const OUTCOME_LABEL: Record<RecordOutcome, string> = {
  wins: 'Wins',
  losses: 'Losses',
  ties: 'Ties',
  otLosses: 'Overtime Losses',
  soLosses: 'Shootout Losses',
}

export interface RecordSegment {
  key: RecordOutcome
  label: string
  /** Count of games with this outcome. Always > 0. */
  value: number
  /** Left edge in px. */
  x: number
  /** Width in px. */
  width: number
  roundStart: boolean
  roundEnd: boolean
}

/**
 * Lay a parsed record out as positioned bar segments.
 *
 * Outcomes with a zero count are dropped, so a 1-0-0-0-0 record is a single
 * solid bar. Gap width is removed from the drawable span BEFORE distributing
 * it — adding gaps afterwards would push the last segment past `width` and
 * clip its rounded end.
 */
export const buildRecordSegments = (
  record: InDivisionRecord,
  width: number
): RecordSegment[] => {
  if (!(width > 0)) return []

  const present = RECORD_OUTCOMES.map((key) => ({
    key,
    value: record[key],
  })).filter((outcome) => outcome.value > 0)

  if (present.length === 0) return []

  const drawable = width - (present.length - 1) * RECORD_SEGMENT_GAP
  if (!(drawable > 0)) return []

  let x = 0

  return present.map((outcome, index) => {
    const segmentWidth = (outcome.value / record.gamesPlayed) * drawable
    const segment: RecordSegment = {
      key: outcome.key,
      label: OUTCOME_LABEL[outcome.key],
      value: outcome.value,
      x,
      width: segmentWidth,
      // Only the outer ends are rounded, so the stack reads as one bar.
      roundStart: index === 0,
      roundEnd: index === present.length - 1,
    }
    x += segmentWidth + RECORD_SEGMENT_GAP
    return segment
  })
}

/**
 * Bar length as a percentage of the track, scaled against the division's
 * busiest team — so a 1-0-0-0-0 team never looks like a 9-0-0-0-0 team.
 */
export const recordBarWidthPercent = (
  gamesPlayed: number,
  maxGamesPlayed: number
): number => {
  if (!(maxGamesPlayed > 0)) return 100

  return Math.max(MIN_BAR_WIDTH_PERCENT, (gamesPlayed / maxGamesPlayed) * 100)
}
